"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

function getWorkspaceSegmentFromPath(pathname) {
  if (!pathname) return null;
  const workspacesIndex = pathname.indexOf("/workspaces/");
  if (workspacesIndex === -1) return null;

  const segmentStart = workspacesIndex + "/workspaces/".length;
  const segment = pathname.slice(segmentStart).split("/")[0];
  return segment || null;
}

function getWorkspaceIdFromPath(pathname, knownWorkspaceIds) {
  const segment = getWorkspaceSegmentFromPath(pathname);
  if (!segment) return null;
  return knownWorkspaceIds.has(segment) ? segment : null;
}

/**
 * Where to land after switching studios: the same page in the new studio when
 * the current URL names one, otherwise wherever we already are.
 */
export function buildWorkspaceSwitchUrl(
  pathname,
  searchParams,
  targetWorkspaceId,
  currentPathWorkspaceId,
) {
  if (!pathname || !targetWorkspaceId) return "/";

  const nextPathname = currentPathWorkspaceId
    ? pathname.replace(
        `/workspaces/${currentPathWorkspaceId}`,
        `/workspaces/${targetWorkspaceId}`,
      )
    : pathname;

  const queryString = searchParams.toString();
  return `${nextPathname}${queryString ? `?${queryString}` : ""}`;
}

/**
 * The studio switcher's state: which studios this person may open, which one is
 * showing, and how to move between them.
 *
 * The switch is deliberately a full page load rather than a client navigation —
 * every server component on the page was rendered for the old studio.
 */
export function useWorkspaceSwitcher({ session, update }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [isSwitchingWorkspace, setIsSwitchingWorkspace] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");

  const isSuperUser = session?.user?.superUser;
  const isAdmin = session?.user?.role === "admin";
  const sessionWorkspaceId =
    session?.workspace?.id || session?.user?.workspaceId || "";

  const knownWorkspaceIds = useMemo(() => {
    const ids = [
      sessionWorkspaceId,
      selectedWorkspaceId,
      ...availableWorkspaces.map((workspace) => workspace.id),
    ].filter(Boolean);
    return new Set(ids);
  }, [availableWorkspaces, selectedWorkspaceId, sessionWorkspaceId]);

  const pathWorkspaceId = getWorkspaceIdFromPath(pathname, knownWorkspaceIds);

  const switcherCurrentWorkspace =
    availableWorkspaces.find(
      (workspace) => workspace.id === selectedWorkspaceId,
    ) ||
    availableWorkspaces.find((workspace) => workspace.id === pathWorkspaceId) ||
    availableWorkspaces.find(
      (workspace) => workspace.id === sessionWorkspaceId,
    ) ||
    session?.workspace;

  useEffect(() => {
    // Only superUsers and admins can be in more than one studio.
    if (!(isSuperUser || isAdmin) || !session?.user?.id) return;

    const userId = session.user.id;
    let cancelled = false;

    // Home plus the allowed list, for superUsers too: switching takes no
    // other studio (lib/workspaces/studio-reach.js).
    fetch(`/api/users/${userId}/allowed-workspaces`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setAvailableWorkspaces(
          data.sort((a, b) => a.name.localeCompare(b.name)),
        );
      })
      .catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [isSuperUser, isAdmin, session?.user?.id]);

  useEffect(() => {
    if (isSwitchingWorkspace) return;

    const effectiveWorkspaceId = pathWorkspaceId || sessionWorkspaceId;
    if (!effectiveWorkspaceId) {
      if (selectedWorkspaceId) {
        setSelectedWorkspaceId("");
      }
      return;
    }

    if (effectiveWorkspaceId !== selectedWorkspaceId) {
      setSelectedWorkspaceId(effectiveWorkspaceId);
    }
  }, [
    isSwitchingWorkspace,
    pathWorkspaceId,
    selectedWorkspaceId,
    sessionWorkspaceId,
  ]);

  const handleWorkspaceSwitch = async (workspace) => {
    if (!workspace?.id || isSwitchingWorkspace) return;

    const previousWorkspaceId = pathWorkspaceId || sessionWorkspaceId;
    if (workspace.id === previousWorkspaceId) {
      setSelectedWorkspaceId(workspace.id);
      return;
    }

    setIsSwitchingWorkspace(true);
    setSelectedWorkspaceId(workspace.id);

    try {

      const updatedSession = await update({ workspaceId: workspace.id });
      const updatedWorkspaceId =
        updatedSession?.workspace?.id || updatedSession?.user?.workspaceId;

      // Re-fetch once if the first update response is stale.
      if (updatedWorkspaceId !== workspace.id) {
        const refreshedSession = await update();
        const refreshedWorkspaceId =
          refreshedSession?.workspace?.id ||
          refreshedSession?.user?.workspaceId;

        if (refreshedWorkspaceId !== workspace.id) {
          throw new Error(
            "Studio switch did not persist to the active session",
          );
        }
      }

      window.location.assign(
        buildWorkspaceSwitchUrl(
          pathname,
          searchParams,
          workspace.id,
          pathWorkspaceId,
        ),
      );
    } catch (error) {
      console.error("Failed to switch workspace:", error);
      setSelectedWorkspaceId(previousWorkspaceId || "");
      setIsSwitchingWorkspace(false);
      router.refresh();
    }
  };

  return {
    availableWorkspaces,
    handleWorkspaceSwitch,
    isSwitchingWorkspace,
    selectedWorkspaceId,
    switcherCurrentWorkspace,
  };
}
